import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { R2_COST_BUCKET } from "../../utils/envConfig";
import CollapsibleSection from "../Billing/CollapsibleSection";
import SectionHeading from "./SectionHeading";

// Same-origin proxy to the R2 cost monitor (Vite locally, nginx deployed); the
// monitor sends no CORS headers, so the browser cannot call it directly.
const R2_COST_API = "/api/r2-cost";

// The monitor treats a missing `from` as the last 24h, so "All" is sent as the
// longest window it has been seen to serve (89 days worked; 90+ did not).
const MAX_WINDOW_DAYS = 89;
const MAX_WINDOW_MS = MAX_WINDOW_DAYS * 24 * 60 * 60_000;

interface R2WorkspaceUsage {
  workspace: string;
  name?: string;
  objects?: number;
  bytes?: number;
  byteShare?: number;
  writes?: number;
  readsEstimated?: number;
  cost?: { storage?: number; classA?: number; classB?: number; total?: number };
}

interface R2WorkspacesResponse {
  bucket: string;
  window: { from: string; to: string };
  workspaces: R2WorkspaceUsage[];
  readsSource?: string;
  unavailable?: string;
}

const integer = new Intl.NumberFormat();
const usd = (value?: number) =>
  `$${(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    // R2 costs per workspace are often fractions of a cent.
    maximumFractionDigits: 4,
  })}`;
// Decimal units (1 GB = 1000³ bytes), the units Cloudflare bills R2 storage in.
const formatBytes = (bytes?: number) => {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes || 0;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${units[unit]}`;
};

const headClass =
  "bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]";
const cellClass =
  "font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]";

export default function R2Usage({
  workspaceId,
  startDate,
  endDate,
}: {
  workspaceId: string;
  startDate?: string;
  endDate?: string;
}) {
  const [data, setData] = useState<R2WorkspacesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Guards against a slow response for an old range overwriting a newer one.
  const requestRef = useRef(0);

  const earliest = new Date(Date.now() - MAX_WINDOW_MS).toISOString();
  const clamped = !startDate || startDate < earliest;
  const from = clamped ? earliest : startDate;

  useEffect(() => {
    if (!R2_COST_BUCKET) return;
    const requestId = ++requestRef.current;
    const query = new URLSearchParams({
      bucket: R2_COST_BUCKET,
      from,
      workspace: workspaceId,
    });
    if (endDate) query.set("to", endDate);

    setLoading(true);
    setError("");
    fetch(`${R2_COST_API}/workspaces?${query.toString()}`, {
      credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) {
          // The monitor queries Cloudflare Analytics, whose small shared rate
          // budget runs out after a handful of requests; it then answers 5xx.
          throw new Error(
            response.status >= 500
              ? `The R2 cost monitor is unavailable (${response.status}). It is often rate-limited by Cloudflare Analytics for about 5 minutes; try again shortly.`
              : `R2 usage could not be loaded (${response.status}).`,
          );
        }
        return response.json() as Promise<R2WorkspacesResponse>;
      })
      .then((response) => {
        if (requestId === requestRef.current) setData(response);
      })
      .catch((err: Error) => {
        if (requestId !== requestRef.current) return;
        setData(null);
        setError(err.message || "R2 usage could not be loaded.");
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
    // `from` is derived from startDate; keying on startDate keeps the clamped
    // "All" window from refetching on every render as the clock moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, startDate, endDate]);

  const usage = data?.workspaces.find((row) => row.workspace === workspaceId);
  const stats = [
    {
      label: "R2 cost (USD)",
      value: usage ? usd(usage.cost?.total) : undefined,
    },
    { label: "Stored", value: usage ? formatBytes(usage.bytes) : undefined },
    {
      label: "Objects",
      value: usage ? integer.format(usage.objects || 0) : undefined,
    },
    {
      label: "Writes / reads",
      value: usage
        ? `${integer.format(usage.writes || 0)} / ${integer.format(usage.readsEstimated || 0)}`
        : undefined,
    },
  ];

  let body: React.ReactNode;
  if (!R2_COST_BUCKET) {
    body = (
      <div className="w-full rounded-2xl border border-[#eae7ec] px-6 py-4 text-[14px] text-[#84828e]">
        No R2 bucket is configured. Set VITE_R2_COST_BUCKET in the env file.
      </div>
    );
  } else if (error || data?.unavailable) {
    body = (
      <div className="w-full rounded-2xl border border-[#f1c9c5] bg-[#fef3f2] px-6 py-4 text-[14px] text-[#b42318]">
        {error ||
          `The R2 cost monitor cannot read ${data?.bucket}: ${data?.unavailable}`}
      </div>
    );
  } else if (!usage) {
    body = (
      <div className="w-full rounded-2xl border border-[#eae7ec] px-6 py-10 text-center text-[14px] text-[#84828e]">
        {loading
          ? "Loading R2 usage..."
          : "No R2 usage was recorded for this workspace in the selected date range."}
      </div>
    );
  } else {
    const cost = usage.cost || {};
    const rows = [
      {
        label: "Storage",
        detail: formatBytes(usage.bytes),
        value: cost.storage,
      },
      {
        label: "Class A operations (writes)",
        detail: integer.format(usage.writes || 0),
        value: cost.classA,
      },
      {
        label: "Class B operations (reads)",
        detail: integer.format(usage.readsEstimated || 0),
        value: cost.classB,
      },
    ];
    body = (
      <div className="w-full">
        <CollapsibleSection
          title="Cost breakdown"
          subtitle="Storage, writes and reads"
        >
          <div
            className="bg-[rgba(253,252,253,0.88)] border border-[#eae7ec] rounded-2xl w-full overflow-hidden"
            data-name="R2 usage table"
          >
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b-0">
                  <TableHead className={headClass}>COST ITEM</TableHead>
                  <TableHead className={`${headClass} text-right`}>
                    QUANTITY
                  </TableHead>
                  <TableHead className={`${headClass} text-right`}>
                    COST (USD)
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.label}
                    className="h-[56px] border-b border-[#faf9fb]"
                  >
                    <TableCell className={`px-5 py-3 ${cellClass}`}>
                      {row.label}
                    </TableCell>
                    <TableCell className={`px-5 py-3 text-right ${cellClass}`}>
                      {row.detail}
                    </TableCell>
                    <TableCell className={`px-5 py-3 text-right ${cellClass}`}>
                      {usd(row.value)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="h-[56px] bg-[#faf9fb]">
                  <TableCell className={`px-5 py-3 ${cellClass} font-semibold`}>
                    Total
                  </TableCell>
                  <TableCell
                    className={`px-5 py-3 text-right ${cellClass} text-[#84828e]`}
                  >
                    {`${((usage.byteShare || 0) * 100).toLocaleString(
                      undefined,
                      {
                        maximumFractionDigits: 1,
                      },
                    )}% of bucket storage`}
                  </TableCell>
                  <TableCell
                    className={`px-5 py-3 text-right ${cellClass} font-semibold`}
                  >
                    {usd(cost.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  return (
    <section className="flex w-full flex-col gap-3" data-name="R2 usage">
      <SectionHeading
        title="R2 storage"
        aside={
          R2_COST_BUCKET && (
            <span className="inline-flex h-6 items-center rounded-full bg-[#f2eff3] px-3 text-[12px] text-[#5f5a66]">
              {R2_COST_BUCKET}
            </span>
          )
        }
      />

      <div
        className="grid w-full grid-cols-2 gap-3 md:grid-cols-4"
        data-name="R2 totals"
      >
        {stats.map(({ label, value }) => (
          <div
            key={label}
            className="rounded-2xl border border-[#eae7ec] bg-[rgba(253,252,253,0.88)] px-4 py-3"
          >
            <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px] uppercase">
              {label}
            </div>
            <div className="mt-1 font-['Geist:SemiBold',_sans-serif] font-semibold text-[#211f26] text-[22px] leading-[28px]">
              {loading ? "…" : (value ?? "—")}
            </div>
          </div>
        ))}
      </div>

      {body}

      <p className="text-[12px] leading-[16px] text-[#84828e]">
        {clamped
          ? `Covers the last ${MAX_WINDOW_DAYS} days, the longest window the R2 cost monitor serves.`
          : "Covers the selected date range."}
        {data?.readsSource === "byte-share-estimate" &&
          " Reads are estimated from this workspace's share of stored bytes."}
      </p>
    </section>
  );
}
